// Thermal slip printing (57mm wide, upright/portrait, dynamic height). Isolated in a hidden
// iframe so only the slip reaches the printer. Swap the body of printThermalSlip() for a local
// print bridge later without touching any order logic.
// ONE shared template for the original print (Vista) and for manual reprints of any order source.
const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
const RSD = (n) => new Intl.NumberFormat("sr-RS").format(Math.round(Number(n) || 0)) + " RSD";
const coinsFmt = (n) => new Intl.NumberFormat("sr-RS").format(Math.round(Number(n) || 0));
const isCoinPayment = (o) => String(o?.payment_method || "").toUpperCase() === "PONESI_COINS";

const hhmm = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  // Restaurant-local clock, never the device timezone (a tablet set to UTC must print local time).
  return isNaN(d) ? "" : d.toLocaleTimeString("sr-RS", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Belgrade" });
};

export const sourceLabel = (order) =>
  order?.order_source === "RESTAURANT_INTERNAL" ? "LOKAL" : "PONESI.RS";

function itemsHtml(items) {
  return (items || []).map((it) => {
    const mods = (it.modifiers || []).map((m) => `<div class="mod">${esc(m.name)}</div>`).join("");
    const person = it.person_label ? `<div class="mod b">Za: ${esc(it.person_label)}</div>` : "";
    const note = it.note ? `<div class="mod b">Napomena: ${esc(it.note)}</div>` : "";
    return `<div class="item"><div class="prod">${esc(it.quantity)}x ${esc(it.name)}</div>${mods}${person}${note}</div>`;
  }).join("");
}

// order: { public_code, order_source, created_at, pickup_time, items[], note, total, payment_method }
// opts:  { copy: true } -> adds a subtle KOPIJA mark; the layout itself stays identical.
export function printThermalSlip(order, opts = {}) {
  return new Promise((resolve, reject) => {
    let frame;
    try {
      const created = hhmm(order.created_at);
      const isCoins = isCoinPayment(order);
      frame = document.createElement("iframe");
      frame.setAttribute("aria-hidden", "true");
      frame.style.cssText = "position:fixed;width:0;height:0;border:0;left:-9999px;top:0;";
      document.body.appendChild(frame);
      const doc = frame.contentDocument;
      doc.open();
      doc.write(`<!doctype html><html><head><meta charset="utf-8"><title>slip</title><style>
        @page { size: 57mm auto; margin: 0; }
        html,body { margin:0; padding:0; background:#fff; color:#000; width:57mm; }
        body { box-sizing:border-box; padding:2mm 3mm; font-family: Arial, Helvetica, sans-serif; }
        .c { text-align:center; }
        .brand { font-size:11pt; font-weight:700; }
        .lbl { font-size:7pt; font-weight:700; letter-spacing:1px; margin-top:1.5mm; }
        .code { font-size:30pt; font-weight:900; line-height:1; }
        .hr { border-top:1px dashed #000; margin:2mm 0; }
        .item { margin-bottom:1.5mm; }
        .prod { font-size:10pt; font-weight:900; line-height:1.15; }
        .mod { font-size:8pt; padding-left:3mm; line-height:1.2; }
        .mod.b { font-weight:700; }
        .amt { font-size:14pt; font-weight:900; line-height:1; }
        .pay { font-size:9pt; font-weight:700; }
        .src { font-size:9pt; font-weight:900; letter-spacing:1px; }
        .meta { font-size:8pt; }
        .copy { font-size:8pt; font-weight:900; letter-spacing:2px; border:1px solid #000; display:inline-block; padding:0.5mm 2mm; }
      </style></head><body>
        <div class="c brand">ponesi.rs</div>
        <div class="c src">${esc(sourceLabel(order))}</div>
        ${opts.copy ? `<div class="c" style="margin-top:1mm"><span class="copy">KOPIJA</span></div>` : ""}
        <div class="c lbl">ŠIFRA</div>
        <div class="c code">${esc(order.public_code)}</div>
        ${created || order.pickup_time ? `<div class="c meta">${created ? `Primljeno: ${esc(created)}` : ""}${created && order.pickup_time ? " · " : ""}${order.pickup_time ? `Preuzimanje: ${esc(order.pickup_time)}${order.pickup_type === "asap" ? " (ASAP)" : ""}` : ""}</div>` : ""}
        <div class="hr"></div>
        ${itemsHtml(order.items)}
        ${order.pickup_type === "internal_scheduled" ? `<div class="c lbl">ZAKAZANO ZA ${esc(order.pickup_time || "")}</div>` : ""}
        ${order.note ? `<div class="hr"></div><div class="lbl">NAPOMENA</div><div class="mod b" style="padding-left:0">${esc(order.note)}</div>` : ""}
        <div class="hr"></div>
        <div class="c lbl">IZNOS</div>
        ${isCoins ? `
        <div class="c amt">${esc(coinsFmt(order.coins_redeemed))} COINS</div>
        <div class="c pay" style="margin-top:1.5mm">Način plaćanja: PONESI COINS</div>
        <div class="c meta" style="margin-top:1mm">Potrošeno Coins: ${esc(coinsFmt(order.coins_redeemed))}</div>
        ${order.reference_menu_value_rsd ? `<div class="c meta">Referentna vrednost: ${esc(RSD(order.reference_menu_value_rsd))} (nije uplata)</div>` : ""}
        ` : `
        <div class="c amt">${esc(RSD(order.total))}</div>
        ${order.payment_method ? `<div class="c pay" style="margin-top:1.5mm">Način plaćanja: ${order.payment_method === "card" ? "Kartica" : "Gotovina"}</div>` : ""}
        `}
      </body></html>`);
      doc.close();
      const run = () => {
        try {
          frame.contentWindow.focus();
          frame.contentWindow.print();
          resolve(true);
        } catch (e) {
          reject(e);
        } finally {
          setTimeout(() => frame.remove(), 1000);
        }
      };
      if (frame.contentWindow.document.readyState === "complete") setTimeout(run, 50);
      else frame.onload = () => setTimeout(run, 50);
    } catch (e) {
      if (frame) frame.remove();
      reject(e);
    }
  });
}

/** Manual reprint: always prints from the canonical PERSISTED server snapshot, never from stale
 * frontend state. Print-only — the backend endpoint changes no order state. */
export async function reprintOrder(api, orderId) {
  const { data } = await api.post(`/orders/${orderId}/reprint`);
  await printThermalSlip(data.order, { copy: true });
  return data.order;
}
